/**
 * The fleet: every aircraft this build can fly, and the one place that knows they exist.
 *
 * ## Why a registry rather than an import at the call site
 *
 * Until M6 `main.ts` imported `sparrow.json` directly, which was fine for one aeroplane and is
 * the wrong shape for three: the picker, the last-flight restore, the `?aircraft=` parameter and
 * the spawn logic would each have needed their own opinion about what exists. They now all ask
 * here, and adding a fourth aeroplane is adding a line to one array — which is the promise
 * non-negotiable #8 makes.
 *
 * ## Why the configs are imported statically
 *
 * They are JSON, they total a few tens of kilobytes, and they are needed before anything can be
 * drawn. Lazy-loading them would add a network round trip to the critical path to save less than
 * one satellite tile. The *models* are the thing worth lazy-loading, and CLAUDE.md's payload
 * budget says so specifically — "aircraft glTFs lazy-load per selection" — which is about the
 * geometry, not about the numbers.
 *
 * `updateFixed` / `updateFrame`: neither. Load-time only.
 */

import sparrowJson from './configs/sparrow.json' with { type: 'json' };
import tumblerJson from './configs/tumbler.json' with { type: 'json' };
import vandalJson from './configs/vandal.json' with { type: 'json' };
import learjetJson from './configs/learjet.json' with { type: 'json' };
import warbirdJson from './configs/warbird.json' with { type: 'json' };
import type { AircraftConfigJson } from './config.js';

export interface AircraftEntry {
  id: string;
  name: string;
  /** One line for the picker. */
  summary: string;
  config: AircraftConfigJson;
  /** Does it carry anything? Derived, so it cannot disagree with the config. */
  readonly armed: boolean;
}

function entry(config: AircraftConfigJson, summary: string): AircraftEntry {
  return {
    id: config.id,
    name: config.name,
    summary,
    config,
    armed: config.weapons !== undefined,
  };
}

/*
 * The warbird is **built and validated but not listed yet**, deliberately.
 *
 * `src/aircraft/configs/marauder.json` and `marauder.gates.test.ts` are complete — it stalls at
 * 87 KIAS, climbs 3,155 fpm and holds its published cruise at 25,000 ft. What it does not have is
 * a model, and three tests in this repo correctly refuse to let an aeroplane into the registry
 * without one: `aircraftModels.test.ts` wants a GLB, `modelCredits.test.ts` wants an attribution,
 * and `airframes.test.ts` wants a distinct exterior. Those are the right assertions and weakening
 * any of them to make a half-added aircraft look finished would be the wrong trade.
 *
 * It joins this list in the commit that brings its model.
 */

/** Trainer → aerobat → fighter → warbird → jet: the difficulty order, and the order to learn them. */
export const AIRCRAFT: readonly AircraftEntry[] = [
  entry(
    sparrowJson as unknown as AircraftConfigJson,
    '180 hp trainer · docile, honest, and validated against a C172S',
  ),
  entry(
    tumblerJson as unknown as AircraftConfigJson,
    '300 hp unlimited aerobat · 366°/s of roll and ±10 g',
  ),
  entry(
    vandalJson as unknown as AircraftConfigJson,
    'fly-by-wire fighter · Mach 1.6, 9 g, a gun and four missiles',
  ),
  entry(
    warbirdJson as unknown as AircraftConfigJson,
    '1600 hp radial warbird · representative of the type, not a named one',
  ),
  entry(
    learjetJson as unknown as AircraftConfigJson,
    'twin-turbofan business jet · Mach 0.81 in the low forties',
  ),
];

export const DEFAULT_AIRCRAFT_ID = 'sparrow';

/** Look one up. Falls back to the trainer rather than throwing — a bad id must not break boot. */
export function aircraftById(id: string | null | undefined): AircraftEntry {
  if (typeof id === 'string') {
    const found = AIRCRAFT.find((candidate) => candidate.id === id);
    if (found !== undefined) return found;
  }
  return AIRCRAFT.find((candidate) => candidate.id === DEFAULT_AIRCRAFT_ID) ?? AIRCRAFT[0];
}

/** `?aircraft=vandal` — open straight into a particular aeroplane. */
export function requestedAircraftId(
  search: string = globalThis.location?.search ?? '',
): string | null {
  const value = new URLSearchParams(search).get('aircraft')?.trim().toLowerCase();
  return value === undefined || value === '' ? null : value;
}
